import { normalizedUuidSchema } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMyOrderDetailQuery } from '@/entities/order';
import { useReviewStatesForOrdersQuery } from '@/entities/review';
import { ReviewForm, useSaveReview } from '@/features/review-write';
import { MY_REVIEWS_PATH } from '@/shared/config';
import { SitePageShell } from '@/shared/ui';
import { ReviewPageHeader } from '@/widgets/review-page-header';
import { ReviewPageNotice } from '@/widgets/review-page-notice';
import { ReviewTargetCard } from '@/widgets/review-target-card';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

const BACK_TO_MY_REVIEWS = { to: MY_REVIEWS_PATH, label: '내 후기로 가기' };

/** 후기 쓰기(/my/orders/:orderId/review) — 주문 상세 응답에 상품 id 가 없어 저장 때 요약 전체를 무효화한다. */
export const ReviewWritePage = (): ReactElement => {
  const { orderId = '' } = useParams();
  const parsed = normalizedUuidSchema.safeParse(orderId);
  const id = parsed.success ? parsed.data : '';
  const navigate = useNavigate();
  const order = useMyOrderDetailQuery(id, { enabled: parsed.success });
  const states = useReviewStatesForOrdersQuery(parsed.success ? [id] : []);
  const save = useSaveReview();
  const state = states.data?.get(id);

  const body = ((): ReactElement => {
    if (!parsed.success) {
      return <ReviewPageNotice kind="error" message="후기를 쓸 수 없는 주문이에요." />;
    }
    if (states.isPending) {
      return <ReviewPageNotice kind="loading" message="주문을 확인하는 중이에요" />;
    }
    if (states.data === undefined) {
      return (
        <ReviewPageNotice
          kind="error"
          message="주문을 확인하지 못했어요. 잠시 후 다시 시도해 주세요."
          onRetry={() => void states.refetch()}
        />
      );
    }
    if (state?.kind === 'WRITTEN') {
      return (
        <ReviewPageNotice
          kind="info"
          message="이 주문에는 이미 후기를 썼어요."
          link={BACK_TO_MY_REVIEWS}
        />
      );
    }
    if (state?.kind === 'EXPIRED') {
      return (
        <ReviewPageNotice
          kind="info"
          message="후기 작성 기간(배송 완료 후 90일)이 지났어요."
          link={BACK_TO_MY_REVIEWS}
        />
      );
    }
    if (state?.kind !== 'WRITABLE') {
      return (
        <ReviewPageNotice
          kind="info"
          message="후기를 쓸 수 없는 주문이에요. 배송이 완료된 주문만 후기를 쓸 수 있어요."
          link={BACK_TO_MY_REVIEWS}
        />
      );
    }
    return (
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-16">
        <div className="lg:order-2">
          <ReviewTargetCard
            productName={order.data?.productName ?? '주문한 상품'}
            thumbnailUrl={order.data?.thumbnailUrl ?? null}
            detail={
              order.data === undefined
                ? null
                : `${order.data.quantity.toLocaleString('ko-KR')}개 구매`
            }
          />
        </div>
        <ReviewForm
          submitLabel="등록"
          onSubmit={async (values) => {
            await save.create({ orderId: id, ...values });
            navigate(MY_REVIEWS_PATH);
          }}
        />
      </div>
    );
  })();

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
      <div className="flex flex-col gap-10 sm:gap-12">
        <ReviewPageHeader title="후기 쓰기" />
        {body}
      </div>
    </SitePageShell>
  );
};
